import { api } from "./api";
import type {
  Shed,
  Flock,
  RawMaterial,
  FeedFormula,
  FlockFormula,
  FlockTransfer,
  Supplier,
  Buyer,
  Breed,
} from "@/types/models";

export const shedService = {
  list: () => api.get<Shed[]>("/sheds").then((r) => r.data),
  get: (id: number) => api.get<Shed>(`/sheds/${id}`).then((r) => r.data),
  create: (data: Partial<Shed>) => api.post<Shed>("/sheds", data).then((r) => r.data),
  update: (id: number, data: Partial<Shed>) => api.put<Shed>(`/sheds/${id}`, data).then((r) => r.data),
  deactivate: (id: number) => api.delete(`/sheds/${id}`),
};

export const flockService = {
  list: () => api.get<Flock[]>("/flocks").then((r) => r.data),
  get: (id: number) => api.get<Flock>(`/flocks/${id}`).then((r) => r.data),
  create: (data: Partial<Flock>) => api.post<Flock>("/flocks", data).then((r) => r.data),
  update: (id: number, data: Partial<Flock>) => api.put<Flock>(`/flocks/${id}`, data).then((r) => r.data),
};

export const rawMaterialService = {
  list: () => api.get<RawMaterial[]>("/raw-materials").then((r) => r.data),
  get: (id: number) => api.get<RawMaterial>(`/raw-materials/${id}`).then((r) => r.data),
  create: (data: Partial<RawMaterial>) => api.post<RawMaterial>("/raw-materials", data).then((r) => r.data),
  update: (id: number, data: Partial<RawMaterial>) => api.put<RawMaterial>(`/raw-materials/${id}`, data).then((r) => r.data),
};

export const supplierService = {
  list: () => api.get<Supplier[]>("/suppliers").then((r) => r.data),
  get: (id: number) => api.get<Supplier>(`/suppliers/${id}`).then((r) => r.data),
  create: (data: Partial<Supplier>) => api.post<Supplier>("/suppliers", data).then((r) => r.data),
  update: (id: number, data: Partial<Supplier>) => api.put<Supplier>(`/suppliers/${id}`, data).then((r) => r.data),
};

export const buyerService = {
  list: () => api.get<Buyer[]>("/buyers").then((r) => r.data),
  get: (id: number) => api.get<Buyer>(`/buyers/${id}`).then((r) => r.data),
  create: (data: Partial<Buyer>) => api.post<Buyer>("/buyers", data).then((r) => r.data),
  update: (id: number, data: Partial<Buyer>) => api.put<Buyer>(`/buyers/${id}`, data).then((r) => r.data),
};

export const flockTransferService = {
  list: (params?: { limit?: number; offset?: number }) =>
    // Paginated ({ data, meta }), like every list endpoint that pages.
    api
      .get<{ data: FlockTransfer[] | null }>("/flock-transfers", { params })
      .then((r) => r.data.data ?? []),
  create: (data: {
    flock_id: number;
    transfer_date: string;
    notes?: string;
  }) => api.post<FlockTransfer>("/flock-transfers", data).then((r) => r.data),
};

export const flockFormulaService = {
  list: (flockId: number) =>
    api.get<FlockFormula[]>(`/flocks/${flockId}/formulas`).then((r) => r.data),
  assign: (flockId: number, data: { formula_id: number; start_date: string; notes?: string }) =>
    api.post<FlockFormula>(`/flocks/${flockId}/formulas`, data).then((r) => r.data),
};

export const feedFormulaService = {
  list: () => api.get<FeedFormula[]>("/feed-formulas").then((r) => r.data),
  get: (id: number) => api.get<FeedFormula>(`/feed-formulas/${id}`).then((r) => r.data),
  create: (data: Record<string, unknown>) => api.post<FeedFormula>("/feed-formulas", data).then((r) => r.data),
  update: (id: number, data: Record<string, unknown>) => api.put<FeedFormula & { copied?: boolean }>(`/feed-formulas/${id}`, data).then((r) => r.data),
  delete: (id: number) => api.delete(`/feed-formulas/${id}`).then((r) => r.data),
};

export const masterSyncService = {
  syncAll: () =>
    api
      .get<{
        sheds: Shed[];
        flocks: Flock[];
        raw_materials: RawMaterial[];
        feed_formulas: FeedFormula[];
        suppliers: Supplier[];
        buyers: Buyer[];
      }>("/master/sync")
      .then((r) => r.data),
};

export const breedService = {
  list: () => api.get<Breed[]>("/breeds").then((r) => r.data),
  create: (data: { name: string }) =>
    api.post<Breed>("/breeds", data).then((r) => r.data),
  update: (id: number, data: { name: string }) =>
    api.put<Breed>(`/breeds/${id}`, data).then((r) => r.data),
};
